import { App, Avatar, Button, Card, Col, Descriptions, Form, Input, Row, Space, Tag, Typography } from 'antd';
import { KeyOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/stores/authStore';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ProfilePage() {
  const { message } = App.useApp();
  const user = useAuthStore((s) => s.user);
  const currentEnterprise = useAuthStore((s) => s.currentEnterprise);
  const [form] = Form.useForm<PasswordFormValues>();

  if (!user) return null;

  const handleChangePassword = async () => {
    try {
      const values = await form.validateFields();
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success('Password actualizada correctamente.');
      form.resetFields();
    } catch (err: any) {
      if (err?.errorFields) return;
      // interceptor muestra error
    }
  };

  return (
    <div>
      <Title level={3} style={{ margin: '0 0 24px 0' }}>
        <UserOutlined style={{ marginRight: 12 }} />
        Mi perfil
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Informacion personal">
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Avatar size={96} icon={<UserOutlined />} style={{ backgroundColor: 'var(--color-primary)' }} />
              <Title level={4} style={{ margin: '12px 0 4px 0' }}>
                {user.firstName} {user.lastName}
              </Title>
              <Space direction="vertical" size={4}>
                <Text type="secondary">
                  <MailOutlined style={{ marginRight: 6 }} />
                  {user.email}
                </Text>
                {user.isSuperAdmin && <Tag color="purple">Super Admin</Tag>}
              </Space>
            </div>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="ID">{user.id}</Descriptions.Item>
              <Descriptions.Item label="Nombre completo">{user.firstName} {user.lastName}</Descriptions.Item>
              <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
              <Descriptions.Item label="Tipo">
                {user.isSuperAdmin ? <Tag color="purple">Super Admin</Tag> : <Tag>Usuario</Tag>}
              </Descriptions.Item>
              {currentEnterprise && (
                <>
                  <Descriptions.Item label="Enterprise activa">
                    <Text strong>{currentEnterprise.enterpriseName}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Rol activo">
                    <Tag color="blue">{currentEnterprise.roleNameEs}</Tag>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<Space><KeyOutlined />Cambiar password</Space>}>
            <Form form={form} layout="vertical" autoComplete="off">
              <Form.Item
                label="Password actual"
                name="currentPassword"
                rules={[{ required: true, message: 'Ingresa tu password actual.' }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>

              <Form.Item
                label="Nueva password"
                name="newPassword"
                rules={[
                  { required: true, message: 'Nueva password requerida.' },
                  { min: 8, message: 'Minimo 8 caracteres.' },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                label="Confirmar nueva password"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Confirma la password.' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('Las passwords no coinciden.'));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Button type="primary" onClick={handleChangePassword} icon={<KeyOutlined />}>
                Cambiar password
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
